import {
  faArrowLeft,
  faChevronDown,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Tabs from '@radix-ui/react-tabs'
import {
  TokenMedia,
  useAttributes,
  useCollections,
  useDynamicTokens,
  useTokenActivity,
  useUserTokens,
} from '@reservoir0x/reservoir-kit-ui'
import { paths } from '@reservoir0x/reservoir-sdk'
import { ActivityFilters } from 'components/token/ActivityFilters'
import { spin } from 'components/common/LoadingSpinner'
import { MobileActivityFilters } from 'components/common/MobileActivityFilters'
import { OpenSeaVerified } from 'components/common/OpenSeaVerified'
import Layout from 'components/Layout'
import { Anchor, Box, Button, Flex, Grid, Text } from 'components/primitives'
import { Dropdown } from 'components/primitives/Dropdown'
import { TabsContent, TabsList, TabsTrigger } from 'components/primitives/Tab'
import AttributeCard from 'components/token/AttributeCard'
import FullscreenMedia from 'components/token/FullscreenMedia'
import { PriceData } from 'components/token/PriceData'
import RarityRank from 'components/token/RarityRank'
import { TokenActions } from 'components/token/TokenActions'
import { TokenActivityTable } from 'components/token/ActivityTable'
import { TokenInfo } from 'components/token/TokenInfo'
import { ToastContext } from 'context/ToastContextProvider'
import { useENSResolver, useMarketplaceChain, useMounted } from 'hooks'
import fs from 'fs'
import path from 'path'
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { NORMALIZE_ROYALTIES } from 'pages/_app'
import { useContext, useEffect, useState } from 'react'
import { jsNumberForAddress } from 'react-jazzicon'
import Jazzicon from 'react-jazzicon/dist/Jazzicon'
import { useMediaQuery } from 'react-responsive'
import supportedChains, { DefaultChain } from 'utils/chains'
import fetcher from 'utils/fetcher'
import { ssrApiBase } from 'utils/ssrApiBase'
import { DATE_REGEX, timeTill } from 'utils/till'
import titleCase from 'utils/titleCase'
import { useAccount } from 'wagmi'
import { Head } from 'components/Head'

type Props = InferGetServerSidePropsType<typeof getServerSideProps>

type ActivityTypes = Exclude<
  NonNullable<
    NonNullable<
      Exclude<Parameters<typeof useTokenActivity>['1'], boolean>
    >['types']
  >,
  string
>

const IndexPage: NextPage<Props> = ({ assetId, ssr }) => {
  const assetIdPieces = assetId ? assetId.toString().split(':') : []
  let collectionId = assetIdPieces[0]
  const id = assetIdPieces[1]
  const router = useRouter()
  const { addToast } = useContext(ToastContext)
  const account = useAccount()
  const isMounted = useMounted()
  const isSmallDevice = useMediaQuery({ maxWidth: 900 }) && isMounted
  const [tabValue, setTabValue] = useState('info')
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [activityFiltersOpen, setActivityFiltersOpen] = useState(true)
  const [activityTypes, setActivityTypes] = useState<ActivityTypes>([])
  const [activityNames, setActivityNames] = useState<string[]>([])

  const { proxyApi } = useMarketplaceChain()
  const contract = collectionId ? collectionId?.split(':')[0] : undefined

  const { data: tokens, mutate } = useDynamicTokens(
    {
      tokens: [`${contract}:${id}`],
      includeAttributes: true,
      includeTopBid: true,
      includeQuantity: true,
    },
    {
      fallbackData: [ssr.tokens ? ssr.tokens : {}],
    }
  )

  const token = tokens && tokens[0] ? tokens[0] : undefined
  // The client-side refetch can return a token without attributes, wiping the
  // SSR on-chain fallback. Restore SSR attributes if the client dropped them.
  const ssrToken = (ssr as any)?.tokens?.tokens?.[0]?.token
  if (
    token?.token &&
    (!token.token.attributes || token.token.attributes.length === 0) &&
    ssrToken?.attributes?.length > 0
  ) {
    token.token.attributes = ssrToken.attributes
  }
  const is1155 = token?.token?.kind === 'erc1155'

  const { data: collections } = useCollections(
    {
      id: token?.token?.collection?.id,
    },
    {
      fallbackData: [ssr.collection ? ssr.collection : {}],
    }
  )
  const collection = collections && collections[0] ? collections[0] : null

  const { data: userTokens } = useUserTokens(
    is1155 ? account.address : undefined,
    {
      tokens: [`${contract}:${id}`],
      limit: 20,
    }
  )

  const attributesData = useAttributes(collectionId)

  let countOwned = 0
  if (is1155) {
    countOwned = Number(userTokens?.[0]?.ownership?.tokenCount || 0)
  } else {
    countOwned =
      token?.token?.owner?.toLowerCase() === account?.address?.toLowerCase()
        ? 1
        : 0
  }

  const isOwner = countOwned > 0
  const owner = isOwner ? account?.address : token?.token?.owner
  const { displayName: ownerFormatted } = useENSResolver(token?.token?.owner)

  const tokenName = `${token?.token?.name || `#${token?.token?.tokenId}`}`

  const hasAttributes =
    token?.token?.attributes && token?.token?.attributes.length > 0

  const trigger = (
    <Button
      color="gray3"
      size="small"
      css={{
        justifyContent: 'space-between',
        width: '336px',
        px: '$2',
        py: '$2',
      }}
    >
      {isSmallDevice ? null : (
        <Text style="body1">
          {activityNames.map(titleCase).join(', ') || 'All Events'}
        </Text>
      )}
      <Text css={{ color: '$slate10' }}>
        <FontAwesomeIcon icon={faChevronDown} width={16} height={16} />
      </Text>
    </Button>
  )

  useEffect(() => {
    let tab = tabValue
    const hasAttributesTab = isMounted && isSmallDevice && hasAttributes
    if (hasAttributesTab) {
      tab = 'attributes'
    } else {
      tab = 'info'
    }

    let deeplinkTab: string | null = null
    if (typeof window !== 'undefined') {
      const params = new URL(window.location.href).searchParams
      deeplinkTab = params.get('tab')
    }

    if (deeplinkTab) {
      switch (deeplinkTab) {
        case 'attributes':
          if (hasAttributesTab) {
            tab = 'attributes'
          }
          break
        case 'info':
          tab = 'info'
          break
        case 'activity':
          tab = 'activity'
          break
        case 'listings':
          tab = 'listings'
          break
        case 'offers':
          tab = 'offers'
          break
      }
    }
    setTabValue(tab)
  }, [isSmallDevice])

  useEffect(() => {
    const updatedUrl = new URL(`${window.location.origin}${router.asPath}`)
    updatedUrl.searchParams.set('tab', tabValue)
    router.replace(updatedUrl, undefined, {
      shallow: true,
    })
  }, [tabValue])

  const pageTitle = token?.token?.name
    ? token.token.name
    : `${token?.token?.tokenId} - ${token?.token?.collection?.name}`

  return (
    <Layout>
      <Head
        ogImage={token?.token?.image || collection?.banner}
        title={pageTitle}
        description={collection?.description as string}
      />
      <Flex
        justify="center"
        css={{
          maxWidth: 1320,
          mt: 10,
          pb: 100,
          marginLeft: 'auto',
          marginRight: 'auto',
          px: '$1',
          gap: 20,
          flexDirection: 'column',
          alignItems: 'center',
          '@md': {
            mt: 48,
            px: '$3',
            flexDirection: 'row',
            gap: 40,
            alignItems: 'flex-start',
          },
          '@lg': {
            gap: 80,
          },
        }}
      >
        <Flex
          direction="column"
          css={{
            maxWidth: '100%',
            flex: 1,
            width: '100%',
            '@md': { maxWidth: 445 },
            '@lg': { maxWidth: 520 },
            '@xl': { maxWidth: 620 },
            position: 'relative',
            '@sm': {
              '>button': {
                height: 0,
                opacity: 0,
                transition: 'opacity .3s',
              },
            },
            ':hover >button': {
              opacity: 1,
              transition: 'opacity .3s',
            },
          }}
        >
          <Box
            css={{
              backgroundColor: '$gray3',
              borderRadius: 8,
              button: {
                opacity: 1,
                color: '#fff',
                '& svg, & svg path, & svg circle': {
                  fill: '#fff',
                  stroke: '#fff',
                },
              },
            }}
          >
            {(() => {
              const mediaUrl = token?.token?.media || ''
              const imageUrl = token?.token?.image || ''
              const isAudio = mediaUrl && mediaUrl !== imageUrl
              if (isAudio) {
                return (
                  <Box
                    css={{
                      width: '100%',
                      minHeight: isMounted && isSmallDevice ? 300 : 445,
                      borderRadius: 8,
                      overflow: 'hidden',
                      backgroundColor: '$gray3',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 16,
                      padding: 24,
                    }}
                  >
                    {token?.token?.image && (
                      <img
                        src={token.token.image}
                        alt={token?.token?.name || 'Token image'}
                        style={{ width: '100%', maxWidth: 400, borderRadius: 8 }}
                      />
                    )}
                    <audio controls src={mediaUrl} style={{ width: '100%', maxWidth: 400 }} />
                  </Box>
                )
              }
              return (
              <TokenMedia
                token={token?.token}
                videoOptions={{}}
                imageResolution={'large'}
                style={{
                  width: '100%',
                  height: 'auto',
                  minHeight: isMounted && isSmallDevice ? 300 : 445,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
                onRefreshToken={() => {
                  mutate?.()
                  addToast?.({
                    title: 'Refresh token',
                    description: 'Request to refresh this token was accepted.',
                  })
                }}
              />
              )
            })()}
            <FullscreenMedia token={token} />
          </Box>

          {token?.token?.attributes && !isSmallDevice && (
            <Grid
              css={{
                maxWidth: '100%',
                width: '100%',
                gridTemplateColumns: '1fr 1fr',
                gap: '$3',
                mt: 24,
              }}
            >
              {token?.token?.attributes?.map((attribute) => (
                <AttributeCard
                  key={`${attribute.key}-${attribute.value}`}
                  attribute={attribute}
                  collectionTokenCount={collection?.tokenCount || 0}
                  collectionId={collection?.id}
                />
              ))}
            </Grid>
          )}
        </Flex>
        <Flex
          direction="column"
          css={{
            flex: 1,
            px: '$3',
            width: '100%',
            '@md': {
              px: 0,
              maxWidth: '60%',
              overflow: 'hidden',
            },
          }}
        >
          <Flex justify="between" align="center" css={{ mb: 20 }}>
            <Flex align="center" css={{ mr: '$2', gap: '$2' }}>
              <Link
                href={`/${router.query.chain}/collection/${token?.token?.collection?.id}`}
                legacyBehavior={true}
              >
                <Anchor
                  color="primary"
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '$2',
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} height={16} />
                  <Text css={{ color: 'inherit' }} style="subtitle1" ellipsify>
                    {token?.token?.collection?.name}
                  </Text>
                </Anchor>
              </Link>
              <OpenSeaVerified
                openseaVerificationStatus={
                  collection?.openseaVerificationStatus
                }
              />
            </Flex>
            <Button
              onClick={(e) => {
                if (isRefreshing) {
                  e.preventDefault()
                  return
                }
                setIsRefreshing(true)
                fetcher(
                  `${window.location.origin}/${proxyApi}/tokens/refresh/v1`,
                  undefined,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token: `${contract}:${id}` }),
                  }
                )
                  .then(({ data, response }) => {
                    if (response.status === 200) {
                      addToast?.({
                        title: 'Refresh token',
                        description:
                          'Request to refresh this token was accepted.',
                      })
                    } else {
                      throw data
                    }
                    setIsRefreshing(false)
                  })
                  .catch((e) => {
                    const ratelimit = DATE_REGEX.exec(e?.message)?.[0]

                    addToast?.({
                      title: 'Refresh token failed',
                      description: ratelimit
                        ? `This token was recently refreshed. The next available refresh is ${timeTill(
                            ratelimit
                          )}.`
                        : `This token was recently refreshed. Please try again later.`,
                    })

                    setIsRefreshing(false)
                    throw e
                  })
              }}
              disabled={isRefreshing}
              color="gray3"
              size="xs"
              css={{ cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
            >
              <Box
                css={{
                  animation: isRefreshing
                    ? `${spin} 1s cubic-bezier(0.76, 0.35, 0.2, 0.7) infinite`
                    : 'none',
                }}
              >
                <FontAwesomeIcon icon={faRefresh} width={16} height={16} />
              </Box>
            </Button>
          </Flex>
          <Flex align="center" css={{ gap: '$2' }}>
            <Text style="h4" css={{ wordBreak: 'break-all' }}>
              {tokenName}
            </Text>
          </Flex>
          {token && (
            <>
              {is1155 && countOwned > 0 && (
                <Flex align="center" css={{ mt: '$2' }}>
                  <Text style="subtitle3" color="subtle" css={{ mr: '$2' }}>
                    You own {countOwned}
                  </Text>
                  <Link href={`/portfolio`} legacyBehavior={true}>
                    <Anchor
                      color="primary"
                      weight="normal"
                      css={{ ml: '$1', fontSize: 12 }}
                    >
                      Sell
                    </Anchor>
                  </Link>
                </Flex>
              )}
              {!is1155 && (
                <Flex align="center" css={{ mt: '$2' }}>
                  <Text style="subtitle3" color="subtle" css={{ mr: '$2' }}>
                    Owner
                  </Text>
                  <Jazzicon
                    diameter={16}
                    seed={jsNumberForAddress(owner || '')}
                  />
                  <Link href={`/portfolio/${owner}`} legacyBehavior={true}>
                    <Anchor color="primary" weight="normal" css={{ ml: '$1' }}>
                      {isMounted ? ownerFormatted : ''}
                    </Anchor>
                  </Link>
                </Flex>
              )}
              <RarityRank
                token={token}
                collection={collection}
                collectionAttributes={attributesData?.data}
              />
              <PriceData token={token} />
              {isMounted && (
                <TokenActions token={token} isOwner={isOwner} />
              )}
              <Tabs.Root
                value={tabValue}
                onValueChange={(value) => setTabValue(value)}
                style={{
                  paddingRight: isSmallDevice ? 0 : 15,
                }}
              >
                <TabsList
                  css={{
                    overflowX: isSmallDevice ? 'scroll' : 'unset',
                  }}
                >
                  {isMounted && isSmallDevice && hasAttributes && (
                    <TabsTrigger value="attributes">Attributes</TabsTrigger>
                  )}
                  <TabsTrigger value="info">Info</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>
                <TabsContent value="attributes">
                  {token?.token?.attributes && (
                    <Grid
                      css={{
                        gap: '$3',
                        mt: 24,
                        gridTemplateColumns: '1fr',
                        '@sm': {
                          gridTemplateColumns: '1fr 1fr',
                        },
                      }}
                    >
                      {token?.token?.attributes?.map((attribute) => (
                        <AttributeCard
                          key={`${attribute.key}-${attribute.value}`}
                          attribute={attribute}
                          collectionTokenCount={collection?.tokenCount || 0}
                          collectionId={collection?.id}
                        />
                      ))}
                    </Grid>
                  )}
                </TabsContent>
                <TabsContent value="info">
                  {collection && (
                    <TokenInfo token={token} collection={collection} />
                  )}
                </TabsContent>
                <TabsContent value="activity" css={{ mr: -15 }}>
                  {isSmallDevice ? (
                    <MobileActivityFilters
                      activityTypes={activityTypes}
                      setActivityTypes={setActivityTypes}
                    />
                  ) : (
                    <Dropdown
                      trigger={trigger}
                      contentProps={{
                        sideOffset: 8,
                      }}
                    >
                      <ActivityFilters
                        open={activityFiltersOpen}
                        setOpen={setActivityFiltersOpen}
                        activityTypes={activityTypes}
                        activityNames={activityNames}
                        setActivityNames={setActivityNames}
                        setActivityTypes={setActivityTypes}
                      />
                    </Dropdown>
                  )}
                  <TokenActivityTable
                    id={`${contract}:${token?.token?.tokenId}`}
                    activityTypes={activityTypes}
                  />
                </TabsContent>
              </Tabs.Root>
            </>
          )}
        </Flex>
      </Flex>
    </Layout>
  )
}

type SSRProps = {
  collection?:
    | paths['/collections/v7']['get']['responses']['200']['schema']
    | null
  tokens?: paths['/tokens/v6']['get']['responses']['200']['schema'] | null
}

// In-memory cache for on-chain token metadata. tokenURI is immutable
// on-chain and IPFS metadata never changes, so this is safe to cache
// for the lifetime of the server process.
const onchainTokenCache = new Map<string, any>()

export const getServerSideProps: GetServerSideProps<{
  assetId?: string
  ssr: SSRProps
}> = async ({ params, res }) => {
  const t0 = Date.now()
  const tlog = (stage: string, t: number) => console.log(`[timing] asset ${stage}: ${Date.now() - t}ms`)
  const assetId = params?.assetId ? params.assetId.toString().split(':') : []
  let collectionId = assetId[0]
  const id = assetId[1]
  const ssrChain =
    supportedChains.find((chain) => params?.chain === chain.routePrefix) ||
    DefaultChain
  // Server-side data now comes from the Alchemy translation layer, which keeps
  // the Reservoir JSON contract the UI expects.
  const ssrApi = ssrApiBase(ssrChain.routePrefix)

  const contract = collectionId ? collectionId?.split(':')[0] : undefined

  let tokensQuery: paths['/tokens/v6']['get']['parameters']['query'] = {
    tokens: [`${contract}:${id}`],
    includeAttributes: true,
    includeTopBid: true,
    normalizeRoyalties: NORMALIZE_ROYALTIES,
    includeDynamicPricing: true,
  }

  let tokens: SSRProps['tokens'] = null

  // Drop-record image override: the admin can set a custom image per drop in
  // config/drops.json; it takes priority over API/on-chain metadata images.
  let dropImage = undefined
  const tDrop = Date.now()
  try {
    const rawDrops = fs.readFileSync(path.join(process.cwd(), 'config', 'drops.json'), 'utf-8')
    const dropsArr = JSON.parse(rawDrops)
    if (Array.isArray(dropsArr)) {
      const match = dropsArr.find(
        (d) => d.contractAddress?.toLowerCase() === contract?.toLowerCase() &&
               String(d.tokenId) === String(id)
      )
      if (match?.image) {
        dropImage = String(match.image).startsWith('ipfs://')
          ? match.image.replace('ipfs://', 'https://gateway.pinata.cloud/ipfs/')
          : match.image
      }
    }
  } catch {}
  tlog('drop-record', tDrop)
  let collection: SSRProps['collection'] = null

  try {
    const tTokens = Date.now()
    const tokensPromise = fetcher(`${ssrApi}/tokens/v6`, tokensQuery)

    const tokensResponse = await tokensPromise
    tlog('tokens-api', tTokens)
    tokens = tokensResponse.data
      ? (tokensResponse.data as Props['ssr']['tokens'])
      : {}

    let collectionQuery: paths['/collections/v7']['get']['parameters']['query'] =
      {
        id: tokens?.tokens?.[0]?.token?.collection?.id,
        normalizeRoyalties: NORMALIZE_ROYALTIES,
      }

    const tColl = Date.now()
    const collectionsPromise = fetcher(`${ssrApi}/collections/v7`, collectionQuery)

    const collectionsResponse = await collectionsPromise
    tlog('collections-api', tColl)
    collection = collectionsResponse.data
      ? (collectionsResponse.data as Props['ssr']['collection'])
      : {}

    res.setHeader(
      'Cache-Control',
      'public, s-maxage=30, stale-while-revalidate=60'
    )
  } catch (e) {}

  const tChain = Date.now()
  // On-chain fallback: if the API returned no token data, read tokenURI
  // directly from the contract and build the token object from IPFS metadata.
  // This powers the attributes grid for collections the indexer doesn't cover.
  // Runs when the API has no token OR when it has one without attributes.
  const apiToken = tokens?.tokens?.[0]?.token
  if (
    (!apiToken || !apiToken.attributes || apiToken.attributes.length === 0) &&
    contract &&
    id
  ) {
    const cacheKey = `${contract}:${id}`.toLowerCase()
    const cached = onchainTokenCache.get(cacheKey)
    if (cached) {
      tlog('onchain-cache-hit', tChain)
      tokens = cached
    } else
    try {
      const { createPublicClient, http } = await import('viem')
      const { polygon } = await import('viem/chains')
      const { SINGLES_ABI } = await import('utils/editions')
      const client = createPublicClient({
        chain: polygon,
        transport: http('https://polygon-bor-rpc.publicnode.com'),
      })
      const tRpc = Date.now()
      const uri = (await client.readContract({
        address: contract as `0x${string}`,
        abi: SINGLES_ABI,
        functionName: 'tokenURI',
        args: [BigInt(id)],
      })) as string
      tlog('rpc-tokenURI', tRpc)
      const gateway = uri.replace(
        'ipfs://',
        'https://gateway.pinata.cloud/ipfs/'
      )
      const tIpfs = Date.now()
      const metaRes = await fetch(gateway)
      tlog('ipfs-metadata', tIpfs)
      const meta = await metaRes.json()
      const attrs = Array.isArray(meta.attributes)
        ? meta.attributes.map((a: any) => ({
            key: a.trait_type || a.key || '',
            value: a.value ?? '',
            kind: 'string',
          }))
        : []
      tokens = {
        tokens: [
          {
            token: {
              // Preserve API data (owner, prices, etc.), fill gaps from chain.
              ...apiToken,
              contract,
              tokenId: id,
              name: apiToken?.name || meta.name || `#${id}`,
              description: apiToken?.description || meta.description || undefined,
              image: dropImage
                ? dropImage
                : apiToken?.image
                ? apiToken.image
                : meta.image
                ? meta.image.replace(
                    'ipfs://',
                    'https://gateway.pinata.cloud/ipfs/'
                  )
                : undefined,
              media: (apiToken as any)?.media
                ? (apiToken as any).media
                : meta.animation_url
                ? meta.animation_url.replace(
                    'ipfs://',
                    'https://gateway.pinata.cloud/ipfs/'
                  )
                : undefined,
              attributes: attrs,
              collection: { id: contract },
            },
          },
        ],
      } as any
      onchainTokenCache.set(cacheKey, tokens)
    } catch (e) {}
  }

  tlog('onchain-fallback', tChain)
  tlog('total-gssp', t0)
  // Apply drop-record image override to API-sourced tokens as well
  if (dropImage && tokens?.tokens?.[0]?.token) {
    tokens.tokens[0].token.image = dropImage
  }
  // Apply drop-record image to collection when Alchemy has none
  if (dropImage && collection?.collections?.[0] && !collection.collections[0].image) {
    collection.collections[0].image = dropImage
  }

  return {
    props: {
      assetId: params?.assetId as string,
      ssr: { collection, tokens },
    },
  }
}

export default IndexPage
